import React from 'react';
import { Link } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { jsPDF } from 'jspdf';
import { formSections } from '../data/intakeFormSection';
import Layout from '../components/Layout';
//import axlLogo from '../assets/axl.png';
import Logo from '../components/Logo';

interface Props {
  session: Session;
}

interface FormData {
  [key: string]: string;
}

const Confirmation: React.FC<Props> = ({ session }) => {
  const generatePDF = () => {
    // Get form data from localStorage
    const formData = JSON.parse(localStorage.getItem('formData') || '{}') as FormData;
    
    // Create new PDF document
    const doc = new jsPDF();
    
    // Set font styles
    doc.setFontSize(20);
    const companyName = formData['company_name'] || 'Unnamed Company';
    doc.text(`Pitch Deck Submission - ${companyName}`, 20, 20);
    
    // Reset font size for content
    doc.setFontSize(12);
    
    // Add content with proper spacing
    let yPosition = 40;
    const lineHeight = 5;
    const pageHeight = 280;
    const margin = 20;
    const contentWidth = 170;
    
    // Helper function to add text with word wrap and page breaks
    const addText = (title: string, content: string, isHeader: boolean = false) => {
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }

      if (isHeader) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(title, 20, yPosition);
        yPosition += lineHeight + 2;
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        
        const splitTitle = doc.splitTextToSize(title, contentWidth);
        doc.text(splitTitle, 20, yPosition);
        yPosition += (splitTitle.length * lineHeight);
        
        doc.setFont('helvetica', 'normal');
        const splitContent = doc.splitTextToSize(content || 'No response provided', contentWidth);
        
        const contentHeight = splitContent.length * lineHeight;
        if (yPosition + contentHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.text(splitContent, 20, yPosition);
        yPosition += (splitContent.length * lineHeight) + 8;
      }
    };

    // Add all form fields to PDF in the correct order
    formSections.forEach((section, sectionIndex) => {
      if (sectionIndex > 0) {
        yPosition += 15;
      }

      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(1);
      doc.line(20, yPosition - 5, 190, yPosition - 5);
      yPosition += 8;
      addText(section.title, '', true);
      yPosition += 6;

      section.categories.forEach(category => {
        if (category.title) {
          addText(category.title, '', true);
          yPosition += 3;
        }

        category.subCategories.forEach(subCategory => {
          if (subCategory.id === 'basic_info') {
            return;
          }

          if (subCategory.title) {
            addText(subCategory.title, '', true);
            yPosition += 3;
          }

          subCategory.fields.forEach(field => {
            if (field.label) {
              addText(field.label, formData[field.id]);
            }
          });
        });
      });
    });
    
    doc.save(`pitch_deck_submission_${companyName.toLowerCase().replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 h-12 bg-[#2a2a4a] flex items-center pl-8 z-50 w-full">
        <div className="flex items-center cursor-pointer" onClick={() => window.location.href = '/'}>
        <Logo background="light" className="h-4 w-auto" onClick={() => window.location.href = '/'} />
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex items-center justify-center pt-16 px-4">
        <div className="max-w-4xl w-full">
          {/* Success Animation */}
          <div className="flex justify-center mb-12">
            <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Main Content */}
          <div className="text-center space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">Submission Successful!</h1>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Thank you for submitting your pitch deck and form responses. Our team will review your submission and get back to you shortly.
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <button 
                onClick={generatePDF}
                className="px-8 py-3 bg-[#57547B] text-white rounded-xl hover:bg-[#4a4866] transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Responses
              </button>

              <button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-white text-[#57547B] border-2 border-[#57547B] rounded-xl hover:bg-[#57547B] hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation; 